import { makeStyles } from "@material-ui/core";
import {
  G, G_SOFT, INK, BODY, MUTED, LINE, WARN, MONO, PAGE_BG, PANEL_BG, fieldReset,
} from "./tokens";

// The studio's whole style sheet. Kept apart from the markup so the step
// components read as structure, and so a class is defined once for all of them.

const useStyles = makeStyles(() => ({
  // ProjectsLayout's route container is height:100vh + overflow:hidden, so this
  // page owns its scrolling or everything below the fold gets clipped.
  page: { height: "100vh", overflowY: "auto", backgroundColor: PAGE_BG },
  back: {
    display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none",
    padding: 0, cursor: "pointer", fontFamily: MONO, fontSize: 11, letterSpacing: "0.1em",
    textTransform: "uppercase", color: MUTED, "&:hover": { color: "#0f7a3d" },
  },
  topbar: {
    position: "sticky", top: 0, zIndex: 5, display: "flex", alignItems: "center",
    justifyContent: "space-between", gap: 16, padding: "12px 22px",
    backgroundColor: "#fff", borderBottom: `1px solid ${LINE}`,
  },
  topTitle: { fontSize: 16, fontWeight: 700, letterSpacing: "-0.01em", color: INK },
  pills: { display: "flex", gap: 6 },
  pill: {
    padding: "5px 12px", borderRadius: 999, border: `1px solid ${LINE}`, background: "#fff",
    color: BODY, cursor: "pointer", fontSize: 12.5, fontWeight: 600, transition: ".15s",
    "&:hover": { borderColor: "#c6d3cc" },
  },
  pillOn: { background: G_SOFT, borderColor: G, color: "#0f7a3d" },
  pillDisabled: { opacity: 0.45, cursor: "not-allowed", display: "inline-block" },

  histWrap: { marginTop: 16 },
  histTitle: { fontFamily: MONO, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: MUTED, marginBottom: 8 },
  histTable: { border: `1px solid ${LINE}`, borderRadius: 10, overflow: "hidden" },
  histRow: {
    display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 8, padding: "9px 12px",
    fontSize: 12.5, color: BODY, borderTop: `1px solid ${LINE}`, fontVariantNumeric: "tabular-nums",
    "&:first-child": { borderTop: "none" },
  },
  histHead: { background: "#eff3f1", color: MUTED, fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.04em", textTransform: "uppercase" },

  wrap: { display: "grid", gridTemplateColumns: "1.35fr 1fr", alignItems: "start",
    "@media (max-width:960px)": { gridTemplateColumns: "1fr" } },
  builder: { padding: "22px 22px 80px", display: "flex", flexDirection: "column", gap: 18, minWidth: 0 },
  preview: {
    position: "sticky", top: 57, height: "calc(100vh - 57px)", overflow: "auto", padding: 22,
    backgroundColor: PANEL_BG, borderLeft: `1px solid ${LINE}`,
    display: "flex", flexDirection: "column", gap: 16,
    "@media (max-width:960px)": { position: "static", height: "auto", borderLeft: "none", borderTop: `1px solid ${LINE}` },
  },

  section: { background: "#fff", border: `1px solid ${LINE}`, borderRadius: 14, boxShadow: "0 1px 2px rgba(16,24,40,.04)", transition: "opacity .15s" },
  sectionLocked: { opacity: 0.55 },
  sectionErr: { borderColor: "#f3c9c9", boxShadow: "0 1px 2px rgba(198,40,40,.06)" },
  secHead: { display: "flex", alignItems: "center", gap: 11, padding: "15px 18px" },
  secHeadOpen: { borderBottom: `1px solid ${LINE}` },
  secNum: {
    width: 24, height: 24, borderRadius: 7, background: "#eef2f0", color: MUTED,
    fontFamily: MONO, fontWeight: 700, fontSize: 12.5, display: "grid", placeItems: "center", flex: "none",
  },
  secNumActive: { background: G_SOFT, color: "#0f7a3d" },
  secNumDone: { background: G, color: "#fff" },
  secNumError: { background: "#fdeaea", color: WARN },
  secTitle: { fontWeight: 700, letterSpacing: "-0.01em", fontSize: 15, color: INK, flex: "none" },
  // Fills the space between the title and the trailing slot, so every card's
  // summary ends on the same right edge whatever badge follows it.
  secSub: {
    color: MUTED, fontSize: 12.5, flex: 1, minWidth: 0, textAlign: "right",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  // Width reserved whether or not an Edit button shows, so a completed step and
  // the open one line up instead of shifting by the button's width.
  secTail: { flex: "none", width: 58, display: "flex", justifyContent: "flex-end" },
  editBtn: {
    flex: "none", padding: "4px 12px", borderRadius: 7, cursor: "pointer",
    border: `1px solid ${LINE}`, background: "#fff", color: BODY, fontSize: 12.5, fontWeight: 600,
    transition: "border-color .15s, color .15s",
    "&:hover": { borderColor: G, color: "#0f7a3d" },
  },
  secBody: { padding: "16px 18px", display: "flex", flexDirection: "column", gap: 14 },
  continueRow: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 },
  continueBtn: { backgroundColor: G, color: "#fff", fontWeight: 700, "&:hover": { backgroundColor: "#03a050" } },

  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, "@media (max-width:560px)": { gridTemplateColumns: "1fr" } },
  fieldLabel: { fontSize: 12.5, fontWeight: 600, color: BODY, marginBottom: 6, display: "block" },
  hint: { fontSize: 12, color: MUTED, fontWeight: 500 },
  issueBox: {
    background: "#fdf3f3", border: "1px solid #f3c9c9", borderRadius: 9,
    padding: "10px 12px", display: "flex", gap: 9, alignItems: "flex-start",
  },
  issueIco: { color: WARN, fontSize: 17, flex: "none", marginTop: 1 },
  issueHead: { color: WARN, fontWeight: 700, fontSize: 12.5, marginBottom: 5 },
  issueList: {
    margin: 0, paddingLeft: 16, color: WARN, fontSize: 12.5,
    display: "flex", flexDirection: "column", gap: 3,
  },
  issueOne: { color: WARN, fontSize: 12.5, fontWeight: 600 },

  featList: { display: "flex", flexDirection: "column", gap: 6 },
  featRow: {
    display: "flex", alignItems: "center", gap: 9, background: PANEL_BG, border: `1px solid ${LINE}`,
    borderRadius: 9, padding: "8px 11px",
  },
  tick: { color: G, fontWeight: 800, flex: "none" },
  featTxt: { flex: 1, fontSize: 13.5, color: BODY },
  featAdd: { display: "flex", gap: 8 },

  ent: { border: `1px solid ${LINE}`, borderRadius: 12, overflow: "hidden", background: PANEL_BG },
  entOn: { background: "#fff" },
  entHead: { display: "flex", alignItems: "center", gap: 12, padding: "13px 15px", cursor: "pointer", userSelect: "none" },
  entIco: { width: 34, height: 34, borderRadius: 9, display: "grid", placeItems: "center", flex: "none" },
  entName: { fontWeight: 700, fontSize: 14, color: INK, display: "flex", alignItems: "center", gap: 8 },
  entDesc: { fontSize: 12, color: MUTED, marginTop: 2 },
  when: { fontSize: 10.5, letterSpacing: "0.02em", padding: "2px 8px", borderRadius: 6, fontWeight: 600 },
  sw: { marginLeft: "auto", width: 40, height: 23, borderRadius: 999, background: "#cdd7d1", position: "relative", transition: ".18s", flex: "none" },
  swOn: { background: G },
  knob: { position: "absolute", top: 2, left: 2, width: 19, height: 19, borderRadius: "50%", background: "#fff", transition: ".18s" },
  knobOn: { transform: "translateX(17px)" },
  entBody: { padding: "0 15px 15px", display: "flex", flexDirection: "column", gap: 12 },

  // A labelled divider inside a card, e.g. "Add-ons" over the family toggles.
  subHead: { display: "flex", alignItems: "center", gap: 10, marginTop: 4 },
  subHeadLabel: {
    fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase",
    color: MUTED, whiteSpace: "nowrap",
  },
  subHeadRule: { height: 1, background: LINE, flex: 1 },
  addonStack: { display: "flex", flexDirection: "column", gap: 10 },

  // A stepper, not a free input: flow quantity moves by whole packs, so totals
  // off the ladder (5, 15, 25 …) are unreachable rather than just discouraged.
  stepper: {
    display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start",
    border: `1px solid ${LINE}`, borderRadius: 10, background: PANEL_BG, padding: "8px 10px",
  },
  stepBtn: {
    width: 32, height: 32, borderRadius: 8, flex: "none", cursor: "pointer",
    border: "1px solid #cdd7d1", background: "#fff", color: "#0f7a3d",
    display: "grid", placeItems: "center", fontSize: 17, fontWeight: 700, lineHeight: 1,
    transition: "border-color .15s, color .15s",
    "&:hover:not(:disabled)": { borderColor: G, background: G_SOFT },
    "&:disabled": { color: "#c6d0cb", borderColor: "#e2e9e5", cursor: "default" },
  },
  // Fixed width keeps the buttons still while the number changes width.
  stepVal: { minWidth: 132, textAlign: "center", padding: "0 4px" },
  stepNum: { fontSize: 17, fontWeight: 800, color: INK, fontVariantNumeric: "tabular-nums", lineHeight: 1.25 },
  stepSub: { fontFamily: MONO, fontSize: 10.5, color: MUTED, marginTop: 1 },
  creditRow: { display: "grid", gridTemplateColumns: "minmax(0,1fr) 140px", gap: 10, alignItems: "center" },
  creditName: { fontWeight: 600, fontSize: 13, color: INK },
  creditNote: { fontSize: 11, color: MUTED, marginTop: 1 },

  ptable: { border: `1px solid ${LINE}`, borderRadius: 11, overflow: "hidden" },
  prowHead: {
    display: "grid", gridTemplateColumns: "minmax(0,1fr) 168px 168px 40px", background: "#eff3f1", fontFamily: MONO,
    fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: MUTED,
  },
  prow: { display: "grid", gridTemplateColumns: "minmax(0,1fr) 168px 168px 40px", alignItems: "center", borderTop: `1px solid ${LINE}` },
  pcell: { padding: "10px 12px", minWidth: 0 },
  // Money column headers sit over right-aligned numbers, so they align right too.
  pcellNum: { padding: "10px 12px", minWidth: 0, textAlign: "right" },
  // Baseline-nudged so it reads as part of the header label it follows.
  headInfo: { fontSize: 13, color: MUTED, verticalAlign: "-2px", marginLeft: 4, cursor: "help" },
  plbl: { display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 13.5, color: INK, flexWrap: "wrap" },
  src: { fontSize: 10.5, color: MUTED, fontWeight: 600, background: "#eff3f1", borderRadius: 5, padding: "2px 7px" },
  // Per-unit price for a credit line, sitting inline under its title in the
  // pricing step (the entitlements step only says how many).
  unitWrap: { display: "flex", alignItems: "baseline", gap: 5, marginTop: 6 },
  unitSym: { fontSize: 12.5, color: MUTED, fontWeight: 600 },
  unitNote: { fontSize: 12, color: MUTED, fontWeight: 500, whiteSpace: "nowrap" },
  // A compact tinted field: inline in a caption, an underline alone reads as
  // static text and a full outlined box reads as a control bolted into the cell.
  unitInput: {
    "&.MuiTextField-root": { backgroundColor: "transparent", padding: 0, borderRadius: 0 },
    "&&& .MuiInputBase-input": {
      fontSize: 13, padding: "3px 8px", background: "#f1f5f3", borderRadius: 6,
      border: "1px solid #dbe4df", transition: "border-color .15s, background .15s",
    },
    "&&& .MuiInputBase-input:hover": { borderColor: "#b9c8c1" },
    "&&& .MuiInputBase-input:focus": {
      borderColor: G, background: "#fff", boxShadow: `0 0 0 3px ${G}22`,
    },
    "&&& .Mui-error .MuiInputBase-input": { borderColor: WARN, background: "#fdf3f3" },
    // The box is the affordance; the variant's underline would double up on it.
    "& .MuiInput-underline:before, & .MuiInput-underline:after": { display: "none" },
  },
  // Shared by the money fields and the credits Quantity field. The spinner rules
  // only bite on Quantity (MoneyField renders type="text"); min-width:0 lets the
  // <input> shrink to its cell so long numbers aren't clipped by the outline.
  numInput: {
    "& input[type=number]": { MozAppearance: "textfield" },
    "& input[type=number]::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
    "& input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
    "& .MuiOutlinedInput-root": { minWidth: 0 },
    "& .MuiInputBase-input": { minWidth: 0, textOverflow: "ellipsis" },
    "& .MuiInputAdornment-root": { marginRight: 4 },
    "& .MuiInputAdornment-root p": { color: MUTED, fontSize: 13 },
  },
  // Weight and colour come from MoneyField's `emphasis` prop; the "was"
  // strikethrough lives in the buyer preview, not on the input.
  origInput: {
    "& .MuiInputBase-input::placeholder": { color: MUTED, opacity: 1 },
  },
  emptyLines: { padding: 18, textAlign: "center", color: MUTED, fontSize: 12.5 },
  // Summary rows repeat the table's grid template so their values land on the
  // same axis as the Price column, reading as an invoice total.
  sumRow: {
    display: "grid", gridTemplateColumns: "minmax(0,1fr) 168px 168px 40px",
    alignItems: "center", borderTop: `1px solid ${LINE}`, background: "#fbfdfc",
  },
  sumRowTop: { borderTop: `2px solid #d8e2dd` },
  sumLabel: {
    gridColumn: "1 / 3", padding: "10px 12px", display: "flex", alignItems: "center",
    justifyContent: "flex-end", gap: 5, fontWeight: 700, fontSize: 13, color: INK,
  },
  sumVal: { fontVariantNumeric: "tabular-nums", fontWeight: 800, fontSize: 17, color: INK, textAlign: "right" },

  pvLabel: { fontFamily: MONO, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, fontWeight: 600, marginBottom: 6, display: "flex", alignItems: "center", gap: 7 },
  // "3 lines" beside the billing card's label — known from step 2, so the card
  // says something before any amounts exist.
  pvCount: { background: "#eff3f1", borderRadius: 5, padding: "1px 6px", letterSpacing: "0.04em", color: MUTED },
  // A withheld price renders as an element, not a gap: a dash reads as broken
  // and a ₹0 reads as a real price of zero.
  priceSlot: {
    display: "inline-flex", alignItems: "center", gap: 6, background: G_SOFT,
    border: "1px dashed #9fd9ba", borderRadius: 9, padding: "6px 11px",
    color: "#0f7a3d", fontSize: 12, fontWeight: 650, whiteSpace: "nowrap",
  },
  rowErr: { color: WARN, fontSize: 11.5, fontWeight: 650, marginTop: 5, textAlign: "right", whiteSpace: "nowrap" },

  // ---- locked-after-creation treatment ----
  // The padlock, not grey, carries the meaning: the pricing table already greys
  // computed totals, so grey alone can't say "you may not change this".
  lockIco: { fontSize: 12, color: MUTED, verticalAlign: "-1px", marginLeft: 4 },
  secBadge: {
    display: "inline-flex", alignItems: "center", gap: 5, flex: "none",
    fontFamily: MONO, fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase",
    fontWeight: 700, padding: "3px 9px", borderRadius: 999, background: "#eff3f1", color: MUTED,
  },
  lockBanner: {
    display: "flex", alignItems: "center", gap: 8, background: "#f4f7f5",
    border: `1px solid ${LINE}`, borderRadius: 9, padding: "9px 12px",
    fontSize: 12, color: BODY,
  },
  // A dead switch must not read as an on/off control you simply haven't clicked.
  entLocked: { opacity: 0.62 },
  swDead: { background: "#dbe4df" },
  amtBar: { display: "inline-block", height: 10, borderRadius: 5, background: "#e4eae7" },
  amtBarTot: { height: 13, background: "#d8e2dd" },
  card: { background: "#fff", border: `1px solid ${LINE}`, borderRadius: 14, boxShadow: "0 1px 2px rgba(16,24,40,.04)" },
  ccTitle: { fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em", color: INK },
  ccSub: { color: BODY, fontSize: 13, marginTop: 3 },
  ccPrice: { display: "flex", alignItems: "baseline", gap: 8, margin: "12px 0 0", paddingTop: 12, borderTop: `1px solid ${LINE}` },
  ccBig: { fontSize: 28, fontWeight: 800, color: "#0f7a3d", fontVariantNumeric: "tabular-nums" },
  ccPer: { color: MUTED, fontSize: 13 },
  ccFeats: { listStyle: "none", padding: 0, margin: "12px 0 0", display: "flex", flexDirection: "column", gap: 7 },
  recvItem: { display: "flex", gap: 11, alignItems: "flex-start" },
  recvIco: { width: 28, height: 28, borderRadius: 8, flex: "none", display: "grid", placeItems: "center" },
  bline: { display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", color: BODY },
  blineTot: { borderTop: `1px solid ${LINE}`, marginTop: 6, paddingTop: 9, fontWeight: 800, color: INK },
  // Invoice preview: fixed numeric columns so Title | Qty | Unit | Amount line up
  // across header, lines and total (per-row grids can't share `auto` tracks).
  blineHead: { display: "grid", gridTemplateColumns: "minmax(0,1fr) 78px 104px 104px", columnGap: 12, fontFamily: MONO, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: MUTED, fontWeight: 600, padding: "2px 0 6px" },
  blineGrid: { display: "grid", gridTemplateColumns: "minmax(0,1fr) 78px 104px 104px", columnGap: 12, fontSize: 13, padding: "5px 0", color: BODY, alignItems: "baseline" },
  blineGridTot: { borderTop: `1px solid ${LINE}`, marginTop: 6, paddingTop: 9, fontWeight: 800, color: INK },
  pvNum: { textAlign: "right", fontVariantNumeric: "tabular-nums" },

  // Undoes the app theme's grey field fill and border:none — see fieldReset.
  input: fieldReset,
  removeBtn: { color: MUTED, "&:hover": { color: WARN } },
  createBtn: { backgroundColor: G, color: "#fff", fontWeight: 700, "&:hover": { backgroundColor: "#03a050" }, "&.Mui-disabled": { backgroundColor: "#cdd7d1", color: "#fff" } },
}));

export default useStyles;
